import React, { useMemo } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { formatDateSlashes } from '~/services/TimeService';

import type { ExistingSurchargeDateBlock, ResolvedSurchargeBlock, SalesforceSurchargePeriod } from '~/types/surcharges';

import { daysOfWeekMessage } from '~/utils/daysOfWeek';

import { SurchargeComparisonTable } from './components/SurchargeComparisonTable';

const roomRateLink = (vendorId, propertyId, roomTypeId, roomRateId) => {
  return `/vendors/${vendorId}/properties/${propertyId}/room-types/${roomTypeId}/room-rates/${roomRateId}`;
};

interface Props {
  selectedSurcharges: Array<SalesforceSurchargePeriod>;
  resolvedBlocks: Array<ResolvedSurchargeBlock>;
  selectedRoomRates: Array<{
    propertyId: string;
    uniqueId: string;
    roomType: App.RoomType;
    roomRate: App.RoomRate;
  }>;
  existingSurchargesByRoomRate: Record<string, Array<ExistingSurchargeDateBlock>>;
  propertyId: string;
  vendorId: string;
  setStep: (step: number) => void;
}

export function SalesforceSurchargesSummary({
  selectedSurcharges,
  resolvedBlocks,
  selectedRoomRates,
  existingSurchargesByRoomRate,
  propertyId,
  vendorId,
  setStep,
}: Props) {
  const nameById = useMemo(() => {
    const surchargeNameMap = new Map<string, string>();
    selectedSurcharges.forEach((surcharge) => surchargeNameMap.set(surcharge.id, surcharge.name));
    return surchargeNameMap;
  }, [selectedSurcharges]);

  const hasAnyExisting = useMemo(
    () => Object.values(existingSurchargesByRoomRate).some((dates) => dates.length > 0),
    [existingSurchargesByRoomRate],
  );

  return (
    <Box sx={{ overflow: 'auto', height: '100%' }}>
      {/* Selected Salesforce surcharges */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Selected Salesforce surcharges
          <Button size="small" variant="text" color="primary" onClick={() => setStep(1)} sx={{ ml: 1 }}>
            Reselect
          </Button>
        </Typography>
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" aria-label="selected salesforce surcharges">
              <TableHead
                sx={{
                  bgcolor: 'secondary.dark',
                  '& th': { bgcolor: 'inherit', color: 'white' },
                }}
              >
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Range</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell align="right">Sell</TableCell>
                  <TableCell align="center">Acc</TableCell>
                  <TableCell align="center">Ovl</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedSurcharges.map((surcharge) => (
                  <TableRow key={surcharge.id}>
                    <TableCell>{surcharge.name}</TableCell>
                    <TableCell>
                      {formatDateSlashes(surcharge.start_date)} → {formatDateSlashes(surcharge.end_date)}
                    </TableCell>
                    <TableCell>{daysOfWeekMessage(surcharge)}</TableCell>
                    <TableCell align="right">
                      {surcharge.currency_code} {surcharge.cost_price}
                    </TableCell>
                    <TableCell align="right">{surcharge.sell_price}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={surcharge.accumulate ? 'Y' : 'N'}
                        color={surcharge.accumulate ? 'primary' : 'default'}
                        variant={surcharge.accumulate ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={surcharge.overlapping ? 'Y' : 'N'} variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Selected room rates */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Room Rates
          <Button size="small" variant="text" color="primary" onClick={() => setStep(2)} sx={{ ml: 1 }}>
            Reselect
          </Button>
        </Typography>
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader aria-label="room rates table" size="small">
              <TableHead
                sx={{
                  bgcolor: 'secondary.dark',
                  '& th': { bgcolor: 'inherit', color: 'white' },
                }}
              >
                <TableRow>
                  <TableCell>Room Rate</TableCell>
                  <TableCell align="right">Rate Plan</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedRoomRates.map((roomRate) => (
                  <TableRow key={roomRate.uniqueId} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      <Link
                        href={roomRateLink(vendorId, propertyId, roomRate.roomType.id, roomRate.roomRate.id)}
                        target="_blank"
                      >
                        {roomRate.roomType.name}
                      </Link>
                    </TableCell>
                    <TableCell align="right">{roomRate.roomRate.rate_plan.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Per-room-rate existing vs new comparison */}
      {hasAnyExisting && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Existing vs new surcharges per room rate
          </Typography>
          {selectedRoomRates.map((rr) => {
            const existing = existingSurchargesByRoomRate[rr.roomRate.id] ?? [];
            if (existing.length === 0) {
              return null;
            }
            return (
              <Accordion key={rr.uniqueId} defaultExpanded={selectedRoomRates.length <= 3}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">
                    {rr.roomType.name} — {rr.roomRate.rate_plan.name}
                    <Chip
                      size="small"
                      label={`${existing.length} existing → ${resolvedBlocks.length} new`}
                      color="warning"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <SurchargeComparisonTable
                    existingSurcharges={existing}
                    resolvedBlocks={resolvedBlocks}
                    nameById={nameById}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
